/**
 * The handful of things every console view needs: one fetch hook, the status
 * words of §11.1, and the Hungarian formats of §5.12.
 *
 * The status words are rev7 §11.1's **internal** column, because this is the
 * internal surface. The customer column of the same table is the customer
 * page's and the emails'; using one list for both would be how *véglegesítve*
 * turns into *fagyasztva* on a screen somebody reads out over the phone.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { formatter } from '../page/format.ts'
import { consoleCall, ConsoleError } from './api.ts'
import { useConsole } from './context.ts'

export const fmt = formatter('hu')

/** §11.1, the Internal (HU) column, verbatim. */
export const STATUS_HU: Record<string, string> = {
  submitted: 'beérkezett',
  awaiting_payment: 'fizetésre vár',
  paid: 'fizetve',
  in_review: 'jóváhagyásra vár',
  confirmed: 'visszaigazolva',
  frozen: 'véglegesítve',
  delivered: 'átadva',
  closed: 'lezárva',
  cancelled: 'törölve',
}

/** §11.1's mandatory cancellation reasons. */
export const CANCEL_REASON_HU: Record<string, string> = {
  abandoned: 'félbehagyott',
  payment_expired: 'fizetési határidő lejárt',
  declined: 'elutasítva',
  customer_cancelled: 'vendég lemondta',
  planteen_cancelled: 'a Planteen mondta le',
  admin_cancelled: 'adminisztratív',
}

/** §11.4's review reasons, in the words of its own table. */
export const REVIEW_REASON_HU: Record<string, string> = {
  value: 'értékhatár felett',
  covers: 'vendégszám felett',
  new_customer_value: 'új vendég, értékhatár felett',
  clash_date: 'ütközéses nap',
  zone_c: 'C zóna',
  dietary_note: 'étrendi megjegyzés',
  capacity_soft: 'kapacitás puha küszöb',
}

export const statusText = (status: string, reason?: string | null): string =>
  (STATUS_HU[status] ?? status) +
  (reason ? ` · ${CANCEL_REASON_HU[reason] ?? reason}` : '')

export const asDate = (value: string | null | undefined, weekday = false): string =>
  value === null || value === undefined
    ? '—'
    : fmt.date(new Date(value.length === 10 ? `${value}T00:00:00` : value), weekday)

export function asMoment(value: string | null | undefined): string {
  if (value === null || value === undefined) return '—'
  const d = new Date(value)
  return `${fmt.date(d)} ${String(d.getHours()).padStart(2, '0')}:${
    String(d.getMinutes()).padStart(2, '0')
  }`
}

/**
 * One console reading, with its refusal worded. `reload` exists because every
 * action in §11.7 changes what the list it was launched from should show, and
 * a console that needed a manual refresh to stop offering an order that has
 * already been approved would be a console that gets an order approved twice.
 */
export function useConsoleData<T>(
  action: string,
  args: Record<string, unknown>,
): { data: T | null; error: string | null; loading: boolean; reload: () => void } {
  const { config, session } = useConsole()
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [tick, setTick] = useState(0)
  const key = JSON.stringify(args)

  useEffect(() => {
    let live = true
    setLoading(true)
    consoleCall<T>(action, JSON.parse(key) as Record<string, unknown>, { config, session })
      .then((result) => {
        if (!live) return
        setData(result)
        setError(null)
      })
      .catch((cause: unknown) => {
        if (!live) return
        setError(cause instanceof ConsoleError ? cause.message : 'Nem sikerült betölteni.')
      })
      .finally(() => live && setLoading(false))
    return () => {
      live = false
    }
  }, [action, key, config, session, tick])

  return { data, error, loading, reload: useCallback(() => setTick((n) => n + 1), []) }
}

/** An action, with its refusal shown where it happened rather than thrown. */
export function useConsoleAction(): {
  run: (action: string, args: Record<string, unknown>) => Promise<boolean>
  busy: boolean
  error: string | null
  clear: () => void
  /**
   * Whether the last refusal left the order somewhere else than the screen
   * thinks — `order_moved` on the endpoint's answer.
   *
   * §19.2 principle 2 keeps the provider call outside the transaction, so a
   * money action commits its half of §9.6 and only then asks the provider.
   * When the provider refuses, `run` answers `false` and the view does not
   * reload — which is right for a refused capture, because ruling 4 and §9.10
   * no. 14 leave the order where it was, and wrong for a refused release,
   * because `review_decline` cancelled the order before asking for it. That
   * left a cancelled order in *Jóváhagyásra vár* for a colleague to decline a
   * second time.
   *
   * **A ref and not state**, because a view has to read it on the line after
   * `await run(…)` and a `setState` would not have landed yet.
   */
  moved: { readonly current: boolean }
} {
  const { config, session } = useConsole()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const moved = useRef(false)

  const run = useCallback(
    async (action: string, args: Record<string, unknown>): Promise<boolean> => {
      setBusy(true)
      setError(null)
      moved.current = false
      try {
        await consoleCall(action, args, { config, session })
        return true
      } catch (cause) {
        setError(cause instanceof ConsoleError ? cause.message : 'A művelet nem sikerült.')
        moved.current = cause instanceof ConsoleError && orderMovedFromRefusal(cause.body)
        return false
      } finally {
        setBusy(false)
      }
    },
    [config, session],
  )

  return { run, busy, error, clear: useCallback(() => setError(null), []), moved }
}

/**
 * `order_moved` out of a refusal body, read defensively: anything that is not
 * an explicit `true` is `false`, so a refusal from an older deployment or from
 * an endpoint with no money half reloads nothing.
 *
 * **The whole body, not `result`.** `staff_console` puts `order_moved` beside
 * `result` rather than inside it; reading `result` finds nothing, always.
 * Exported for its test.
 */
export function orderMovedFromRefusal(result: unknown): boolean {
  return (
    typeof result === 'object' &&
    result !== null &&
    (result as { order_moved?: unknown }).order_moved === true
  )
}

export function Loading({ loading, error }: { loading: boolean; error: string | null }): JSX.Element | null {
  if (error !== null) return <p className="note error" data-testid="view-error">{error}</p>
  if (loading) return <p className="note">Betöltés…</p>
  return null
}

export function Tags({ values, tone }: { values: string[]; tone?: 'warn' | 'stop' | 'ok' }): JSX.Element {
  return (
    <>
      {values.map((value) => (
        <span className={`tag${tone ? ` ${tone}` : ''}`} key={value}>{value}</span>
      ))}
    </>
  )
}
